import React, { useState } from "react";
import { ArrowLeft, Avatar, Badge, Button, Card, PageHeader, Send01 } from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { toastError, toastSuccess } from "../../../shared/toastHelper";
import StaffingTabs from "./StaffingTabs";
import PersonDrawer from "./PersonDrawer";
import PositionBadges from "./PositionBadges";
import RecentTimeCards from "./RecentTimeCards";
import { hoursLabel } from "./scheduleFormatters";
import { useStaffingPeopleContext } from "./StaffingPeopleContext";
import { errorMessages, formatPhone, formatRate, invitationSummary, PORTAL_STATUS_COLORS, portalStatusLabel } from "./staffingFormatters";

const UPCOMING_SHOWN = 5;
const TIME_OFF_BADGES = { approved: "success", denied: "error", pending: "gray" };

const textStyle = (size, color, weight = 400) => ({ fontSize: size, lineHeight: `${size + 6}px`, color: `var(--sl-${color})`, fontWeight: weight });
const rowStyle = { display: "flex", justifyContent: "space-between", gap: 12, padding: "10px 0", borderTop: "1px solid var(--sl-border-secondary)" };

const Section = ({ title, aside, children }) => (
  <Card padding="md">
    <div className="d-flex justify-content-between mb-2" style={{ gap: 12 }}>
      <span style={textStyle(16, "text-primary", 600)}>{title}</span>
      {aside && <span style={textStyle(14, "text-tertiary")}>{aside}</span>}
    </div>
    {children}
  </Card>
);

const Row = ({ label, children }) => (
  <div style={rowStyle}>
    <span style={textStyle(14, "text-tertiary")}>{label}</span>
    <span style={{ ...textStyle(14, "text-primary", 600), textAlign: "right", minWidth: 0, overflowWrap: "anywhere" }}>{children}</span>
  </div>
);

const Empty = ({ children }) => <div style={{ ...textStyle(13, "text-tertiary"), padding: "12px 0" }}>{children}</div>;

const hiredLabel = (date) => (date ? new Date(`${date}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const initials = (person) => [person.first_name, person.last_name].map((name) => (name || "").charAt(0)).join("").toUpperCase();

const rateNote = (position) => {
  const parts = [position.primary && "Primary"];
  if (position.volunteer) parts.push("Volunteer");
  else if (position.effective_hourly_rate === undefined) parts.push("can be scheduled");
  else if (position.effective_hourly_rate === null) parts.push("no rate set");
  else parts.push(position.hourly_rate == null ? "position default" : "own rate");
  return parts.filter(Boolean).join(" · ");
};

const PersonProfile = () => {
  const { tabPaths, permissions, paths, person, profile } = useStaffingPeopleContext();
  const [editing, setEditing] = useState(false);
  const [inviting, setInviting] = useState(false);
  const { hours, upcomingShifts, availability, timeOff } = profile;
  const reload = () => window.location.reload();

  const invite = () => {
    setInviting(true);
    client
      .post(paths.invitations, { staff_member_ids: [person.id] })
      .then(({ data }) => {
        const { message, skippedMessage } = invitationSummary(data);
        if (data.invited_ids.length > 0) toastSuccess(message);
        if (skippedMessage) toastError(skippedMessage);
        if (data.invited_ids.length > 0) reload();
      })
      .catch((error) => toastError(errorMessages(error)[0] || "Could not send the invite."))
      .finally(() => setInviting(false));
  };

  const canInvite = permissions.invite && person.can_edit && person.status === "active" && person.portal_status === "not_invited";

  return (
    <>
      <PageHeader title="Staffing" supportingText="Everyone who works your events, paid or volunteer." />
      <div className="mt-3">
        <StaffingTabs current="people" paths={tabPaths} />
      </div>

      <Button variant="link-gray" size="sm" icon={ArrowLeft} onClick={() => (window.location.href = tabPaths.people)}>
        All staff
      </Button>
      <div className="mt-3 mb-4">
        <PageHeader
          avatar={<Avatar size="lg" initials={initials(person)} alt={person.full_name} />}
          title={person.full_name}
          actions={
            person.can_edit && (
              <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                {canInvite && (
                  <Button variant="secondary" icon={Send01} disabled={inviting} onClick={invite}>
                    Send portal invite
                  </Button>
                )}
                {person.can_edit_details && (
                  <Button variant="secondary" onClick={() => setEditing(true)}>
                    {person.pin_set === false ? "Set PIN" : "Reset PIN"}
                  </Button>
                )}
                <Button variant="primary" onClick={() => setEditing(true)}>
                  Edit
                </Button>
              </div>
            )
          }
        />
        <div className="d-flex flex-wrap align-items-center mt-2" style={{ gap: 6 }}>
          <PositionBadges positions={person.positions} />
          <Badge size="sm" color={person.status === "active" ? "success" : "gray"}>
            {person.status === "active" ? "Active" : "Archived"}
          </Badge>
          <Badge size="sm" color={PORTAL_STATUS_COLORS[person.portal_status]}>
            App: {portalStatusLabel(person)}
          </Badge>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))", gap: 16 }}>
        <Section title="Contact">
          {permissions.readContactDetails && (
            <>
              <Row label="Email">{person.email || "—"}</Row>
              <Row label="Phone">{formatPhone(person.phone) || "—"}</Row>
            </>
          )}
          <Row label="Venues">{person.venues.length ? person.venues.map((venue) => venue.name).join(", ") : "—"}</Row>
          {permissions.readContactDetails && (
            <>
              <Row label="Hired">{hiredLabel(person.hired_on)}</Row>
              <Row label="Payroll ID">{person.payroll_external_id || "—"}</Row>
            </>
          )}
        </Section>

        <Section title="Positions and rates">
          {person.positions.length === 0 && <Empty>No positions yet.</Empty>}
          {person.positions.map((position) => (
            <div key={position.id} style={rowStyle}>
              <span className="d-flex flex-column">
                <span style={textStyle(14, "text-primary", 600)}>{position.name}</span>
                <span style={textStyle(12, "text-tertiary")}>{rateNote(position)}</span>
              </span>
              <span style={textStyle(14, "text-secondary", 600)}>{formatRate(position.effective_hourly_rate)}</span>
            </div>
          ))}
        </Section>

        <Section title="Hours">
          <Row label="Scheduled this week">{hoursLabel(hours.scheduledWeekMinutes)} h</Row>
          {hours.workedPeriodMinutes !== null && (
            <>
              <Row label={`Worked ${hours.payPeriodLabel}`}>{hoursLabel(hours.workedPeriodMinutes)} h</Row>
              <Row label="Time cards">{hours.timeCardCount}</Row>
            </>
          )}
        </Section>

        <Section title="Upcoming shifts" aside={upcomingShifts.length > 0 ? `${upcomingShifts.length} total` : undefined}>
          {upcomingShifts.length === 0 && <Empty>Nothing scheduled in the next two weeks.</Empty>}
          {upcomingShifts.slice(0, UPCOMING_SHOWN).map((shift) => (
            <div key={shift.id} style={rowStyle}>
              <span className="d-flex flex-column" style={{ minWidth: 0 }}>
                <span style={textStyle(14, "text-primary", 600)}>{shift.title}</span>
                <span style={textStyle(12, "text-tertiary")}>
                  {shift.date} · {shift.positionName} · {shift.time}
                </span>
              </span>
              <span style={textStyle(12, shift.state === "draft" ? "text-quaternary" : "text-success-primary", 600)}>{shift.state === "draft" ? "Draft" : "Published"}</span>
            </div>
          ))}
        </Section>

        <Section title="Availability">
          {!availability && <Empty>No availability set.</Empty>}
          {availability &&
            availability.map((day) => (
              <Row key={day.day} label={day.day}>
                {day.label}
              </Row>
            ))}
        </Section>

        {timeOff && (
          <Section title="Time off">
            {timeOff.length === 0 && <Empty>No time off requested.</Empty>}
            {timeOff.map((request) => (
              <div key={request.id} style={rowStyle}>
                <span className="d-flex flex-column" style={{ minWidth: 0 }}>
                  <span style={textStyle(14, "text-primary", 600)}>{request.when}</span>
                  {request.reason && <span style={textStyle(12, "text-tertiary")}>{request.reason}</span>}
                </span>
                <Badge size="sm" color={TIME_OFF_BADGES[request.status]}>
                  {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                </Badge>
              </div>
            ))}
          </Section>
        )}

        {paths.timeCards && (
          <Card padding="md">
            <RecentTimeCards staffMemberId={person.id} paths={{ timeCards: paths.timeCards, timeCard: paths.timeCard }} currentPayPeriod />
          </Card>
        )}
      </div>

      {editing && <PersonDrawer person={person} onClose={() => setEditing(false)} onSaved={reload} />}
    </>
  );
};

export default PersonProfile;
